import "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { createCurrentDatetime, fromBackendDate, isInRange } from "@tsp-core/datetime/utils";
import { baseQuery } from "@tsp-core/store/utils";

import { AccountUuid } from "@tsp-domains/accounts/types";
import { parseContractTypeId } from "@tsp-domains/contract-types/utils";
import { CurrencyId } from "@tsp-domains/money/enums";
import { StorableMoney } from "@tsp-domains/money/types";
import { transformFromStorableMoney } from "@tsp-domains/money/utils";
import { PartyUuid } from "@tsp-domains/parties/types";

import { ContractUuid, UserContractRecord, UserContractsList } from "../types";
import { checkIsActiveUserContract, checkIsNotActiveUserContract } from "../utils";

type GetUserContractsResponseData = Record<
  string,
  {
    uuid: ContractUuid;
    createdAt: number;
    currencyId: CurrencyId;
    description: string;
    periodFrom: number;
    periodTo: number | null;
    type: string;
    contractor: {
      uuid: PartyUuid;
      name: string;
      entityType: "company";
      entityUuid: string;
      active: boolean;
      verified: boolean;
    };
    supplier: {
      uuid: PartyUuid;
      name: string;
      entityType: "user";
      entityUuid: string;
      active: boolean;
      verified: boolean;
    };
    supplierAccount: {
      uuid: AccountUuid;
      balance: StorableMoney;
    };
  }
>;

export const contractsApi = createApi({
  reducerPath: "@contracts/api",
  baseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  keepUnusedDataFor: 0,
  tagTypes: ["UserContractsList"],
  endpoints: (builder) => ({
    loadUserContracts: builder.query<UserContractsList, void, GetUserContractsResponseData>({
      query: () => {
        return {
          url: "/getUserContracts",
          method: "GET",
        };
      },
      transformResponse: (response: GetUserContractsResponseData): UserContractsList => {
        const contractsList = Object.values(response)
          .map((contract): UserContractRecord => {
            const periodFrom = fromBackendDate(contract.periodFrom);
            const periodTo = fromBackendDate(contract.periodTo);
            const isActive = periodTo ? isInRange(createCurrentDatetime(), [periodFrom, periodTo]) : true;

            return {
              uuid: contract.uuid,
              title: contract.description,
              createdAt: fromBackendDate(contract.createdAt),
              contractTypeId: parseContractTypeId(contract.type),
              isActive,
              periodFrom: fromBackendDate(contract.periodFrom),
              periodTo: fromBackendDate(contract.periodTo),
              currencyId: contract.currencyId,
              balance: transformFromStorableMoney(contract.supplierAccount.balance),
              contractor: {
                uuid: contract.contractor.uuid,
                name: contract.contractor.name,
              },
            };
          })
          .toSorted((a, b) => a.contractor.name.localeCompare(b.contractor.name));

        return [
          ...contractsList.filter(checkIsActiveUserContract),
          ...contractsList.filter(checkIsNotActiveUserContract),
        ];
      },
    }),
  }),
});
